import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CreditCard, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { divisions } from '../mockData';

const Payment = () => {
  const location = useLocation();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [paymentData, setPaymentData] = useState({
    division: location.state?.division || '',
    plan: location.state?.plan || '',
    serviceDescription: '',
    amount: '',
    paymentMethod: 'credit-card',
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    billingAddress: '',
    city: '',
    state: '',
    zipCode: ''
  });

  const handleChange = (e) => {
    setPaymentData({
      ...paymentData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setPaymentData({
      ...paymentData,
      [name]: value
    });
  };

  const handleNextStep = (e) => {
    e.preventDefault();
    if (step === 1) {
      if (!paymentData.division || !paymentData.amount) {
        toast({
          title: "Missing Information",
          description: "Please fill in all required fields.",
          variant: "destructive"
        });
        return;
      }
      setStep(2);
    } else if (step === 2) {
      // Simulate payment processing
      toast({
        title: "Payment Successful!",
        description: "Your payment has been processed. A confirmation email has been sent.",
      });
      setStep(3);
    }
  };

  const handleBackStep = () => {
    setStep(step - 1);
  };

  return (
    <div className="min-h-screen bg-secondary/30">
      {/* Hero Section */}
      <section className="relative pt-24 md:pt-32 pb-8 md:pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-900/20 via-background to-background">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 md:mb-6">
            Make a <span className="text-primary">Payment</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto px-4">
            Secure and convenient payment processing for all ALM Group services
          </p>
        </div>
      </section>

      {/* Payment Form */}
      <section className="py-8 md:py-12 pb-12 md:pb-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress Steps */}
          <div className="mb-8 md:mb-12">
            <div className="flex items-center justify-center space-x-2 md:space-x-4">
              <div className={`flex items-center ${
                step >= 1 ? 'text-primary' : 'text-muted-foreground'
              }`}>
                <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-semibold text-sm md:text-base ${
                  step >= 1 ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'
                }`}>
                  1
                </div>
                <span className="ml-1 md:ml-2 hidden sm:inline text-sm md:text-base">Service Details</span>
              </div>
              <div className="w-12 md:w-16 h-1 bg-secondary">
                <div className={`h-full transition-all duration-300 ${
                  step >= 2 ? 'bg-primary w-full' : 'bg-secondary w-0'
                }`} />
              </div>
              <div className={`flex items-center ${
                step >= 2 ? 'text-primary' : 'text-muted-foreground'
              }`}>
                <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-semibold text-sm md:text-base ${
                  step >= 2 ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'
                }`}>
                  2
                </div>
                <span className="ml-1 md:ml-2 hidden sm:inline text-sm md:text-base">Payment Info</span>
              </div>
              <div className="w-12 md:w-16 h-1 bg-secondary">
                <div className={`h-full transition-all duration-300 ${
                  step >= 3 ? 'bg-primary w-full' : 'bg-secondary w-0'
                }`} />
              </div>
              <div className={`flex items-center ${
                step >= 3 ? 'text-primary' : 'text-muted-foreground'
              }`}>
                <div className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center font-semibold text-sm md:text-base ${
                  step >= 3 ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'
                }`}>
                  3
                </div>
                <span className="ml-1 md:ml-2 hidden sm:inline text-sm md:text-base">Confirmation</span>
              </div>
            </div>
          </div>

          {/* Step 1: Service Details */}
          {step === 1 && (
            <Card className="shadow-xl shadow-primary/20">
              <CardContent className="p-4 md:p-8">
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6">Service Details</h2>
                <form onSubmit={handleNextStep} className="space-y-4 md:space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Select Service Division *
                    </label>
                    <Select
                      value={paymentData.division}
                      onValueChange={(value) => handleSelectChange('division', value)}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Choose a division" />
                      </SelectTrigger>
                      <SelectContent>
                        {divisions.map((division) => (
                          <SelectItem key={division.id} value={division.name}>
                            {division.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label htmlFor="plan" className="block text-sm font-medium text-foreground mb-2">
                      Plan/Package (Optional)
                    </label>
                    <Input
                      id="plan"
                      name="plan"
                      type="text"
                      value={paymentData.plan}
                      onChange={handleChange}
                      placeholder="e.g., Professional Services, Enterprise Package"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="serviceDescription" className="block text-sm font-medium text-foreground mb-2">
                      Service Description *
                    </label>
                    <Input
                      id="serviceDescription"
                      name="serviceDescription"
                      type="text"
                      required
                      value={paymentData.serviceDescription}
                      onChange={handleChange}
                      placeholder="Brief description of the service"
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="amount" className="block text-sm font-medium text-foreground mb-2">
                      Payment Amount (USD) *
                    </label>
                    <Input
                      id="amount"
                      name="amount"
                      type="number"
                      required
                      min="1"
                      step="0.01"
                      value={paymentData.amount}
                      onChange={handleChange}
                      placeholder="0.00"
                      className="w-full"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-primary hover:bg-primary/90 text-white py-4 md:py-6 text-base md:text-lg shadow-lg shadow-primary/50 hover:shadow-xl shadow-primary/20 transition-all duration-300"
                  >
                    Continue to Payment
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Payment Information */}
          {step === 2 && (
            <Card className="shadow-xl shadow-primary/20">
              <CardContent className="p-4 md:p-8">
                <h2 className="text-xl md:text-2xl font-bold text-foreground mb-4 md:mb-6">Payment Information</h2>
                <form onSubmit={handleNextStep} className="space-y-4 md:space-y-6">
                  {/* Payment Summary */}
                  <div className="bg-primary/10 border border-primary/50 rounded-lg p-4 mb-4 md:mb-6">
                    <h3 className="font-semibold text-foreground mb-2">Payment Summary</h3>
                    <div className="space-y-1 text-sm text-muted-foreground">
                      <div className="flex justify-between">
                        <span>Division:</span>
                        <span className="font-semibold">{paymentData.division}</span>
                      </div>
                      {paymentData.plan && (
                        <div className="flex justify-between">
                          <span>Plan:</span>
                          <span className="font-semibold">{paymentData.plan}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span>Description:</span>
                        <span className="font-semibold">{paymentData.serviceDescription}</span>
                      </div>
                      <div className="flex justify-between text-base md:text-lg font-bold text-primary mt-2 pt-2 border-t border-primary/50">
                        <span>Total Amount:</span>
                        <span>${parseFloat(paymentData.amount).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Payment Method *
                    </label>
                    <div className="grid grid-cols-2 gap-3 md:gap-4">
                      <button
                        type="button"
                        onClick={() => handleSelectChange('paymentMethod', 'credit-card')}
                        className={`p-3 md:p-4 border-2 rounded-lg flex flex-col items-center justify-center transition-all ${
                          paymentData.paymentMethod === 'credit-card'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <CreditCard className="mb-2 text-primary" size={24} />
                        <span className="text-xs md:text-sm font-medium">Credit/Debit Card</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSelectChange('paymentMethod', 'paypal')}
                        className={`p-3 md:p-4 border-2 rounded-lg flex flex-col items-center justify-center transition-all ${
                          paymentData.paymentMethod === 'paypal'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <svg className="mb-2" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20.067 8.478c.492.88.556 2.014.3 3.327-.74 3.806-3.276 5.12-6.514 5.12h-.5a.805.805 0 00-.794.683l-.903 5.727a.688.688 0 01-.679.582h-3.65a.496.496 0 01-.49-.58l2.118-13.423a.804.804 0 01.794-.683h4.084c2.03 0 3.475.427 4.294 1.268.332.342.585.724.74 1.146" fill="#139AD6"/>
                          <path d="M6.31 3.383h7.677c1.448 0 2.48.296 3.073.881.294.29.512.626.651 1.002.145.394.213.846.204 1.353a5.582 5.582 0 01-.124 1.134l-.124.577c-.816 3.99-3.533 5.323-6.999 5.323H8.83a.95.95 0 00-.938.805l-1.003 6.358c-.024.155-.154.267-.31.267H3.423c-.187 0-.33-.17-.296-.352L5.423 4.015c.07-.431.441-.749.88-.749h.007z" fill="#263B80"/>
                        </svg>
                        <span className="text-xs md:text-sm font-medium">PayPal</span>
                      </button>
                    </div>
                  </div>

                  {paymentData.paymentMethod === 'credit-card' && (
                    <>
                      <div>
                        <label htmlFor="cardNumber" className="block text-sm font-medium text-foreground mb-2">
                          Card Number *
                        </label>
                        <Input
                          id="cardNumber"
                          name="cardNumber"
                          type="text"
                          required
                          value={paymentData.cardNumber}
                          onChange={handleChange}
                          placeholder="1234 5678 9012 3456"
                          maxLength="19"
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label htmlFor="cardName" className="block text-sm font-medium text-foreground mb-2">
                          Cardholder Name *
                        </label>
                        <Input
                          id="cardName"
                          name="cardName"
                          type="text"
                          required
                          value={paymentData.cardName}
                          onChange={handleChange}
                          placeholder="John Doe"
                          className="w-full"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3 md:gap-4">
                        <div>
                          <label htmlFor="expiryDate" className="block text-sm font-medium text-foreground mb-2">
                            Expiry Date *
                          </label>
                          <Input
                            id="expiryDate"
                            name="expiryDate"
                            type="text"
                            required
                            value={paymentData.expiryDate}
                            onChange={handleChange}
                            placeholder="MM/YY"
                            maxLength="5"
                            className="w-full"
                          />
                        </div>
                        <div>
                          <label htmlFor="cvv" className="block text-sm font-medium text-foreground mb-2">
                            CVV *
                          </label>
                          <Input
                            id="cvv"
                            name="cvv"
                            type="text"
                            required
                            value={paymentData.cvv}
                            onChange={handleChange}
                            placeholder="123"
                            maxLength="4"
                            className="w-full"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {paymentData.paymentMethod === 'paypal' && (
                    <div className="bg-secondary/30 border border-border rounded-lg p-4 md:p-6">
                      <div className="flex items-center justify-center mb-4">
                        <svg width="120" height="32" viewBox="0 0 120 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M46.211 6.749h-6.839a.95.95 0 00-.939.802l-2.766 17.537a.57.57 0 00.564.658h3.265a.95.95 0 00.939-.803l.746-4.73a.95.95 0 01.938-.803h2.165c4.505 0 7.105-2.18 7.784-6.5.306-1.89.013-3.375-.872-4.415-.972-1.142-2.696-1.746-4.985-1.746zm.789 6.405c-.374 2.454-2.249 2.454-4.062 2.454h-1.032l.724-4.583a.57.57 0 01.563-.481h.473c1.235 0 2.4 0 3.002.704.359.42.468 1.044.332 1.906zM66.654 13.075h-3.275a.57.57 0 00-.563.481l-.145.916-.229-.332c-.709-1.029-2.29-1.373-3.868-1.373-3.619 0-6.71 2.741-7.312 6.586-.313 1.918.132 3.752 1.22 5.031.998 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 00.562.66h2.95a.95.95 0 00.939-.803l1.77-11.209a.568.568 0 00-.561-.658zm-4.565 6.374c-.316 1.871-1.801 3.127-3.695 3.127-.951 0-1.711-.305-2.199-.883-.484-.574-.668-1.391-.514-2.301.295-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.499.589.697 1.411.554 2.317zM84.096 13.075h-3.291a.954.954 0 00-.787.417l-4.539 6.686-1.924-6.425a.953.953 0 00-.912-.678h-3.234a.57.57 0 00-.541.754l3.625 10.638-3.408 4.811a.57.57 0 00.465.9h3.287a.949.949 0 00.781-.408l10.946-15.8a.57.57 0 00-.468-.895z" fill="#253B80"/>
                          <path d="M94.992 6.749h-6.84a.95.95 0 00-.938.802l-2.766 17.537a.569.569 0 00.562.658h3.51a.665.665 0 00.656-.562l.785-4.971a.95.95 0 01.938-.803h2.164c4.506 0 7.105-2.18 7.785-6.5.307-1.89.012-3.375-.873-4.415-.971-1.142-2.694-1.746-4.983-1.746zm.789 6.405c-.373 2.454-2.248 2.454-4.062 2.454h-1.031l.725-4.583a.568.568 0 01.562-.481h.473c1.234 0 2.4 0 3.002.704.359.42.468 1.044.331 1.906zM115.434 13.075h-3.273a.567.567 0 00-.562.481l-.145.916-.23-.332c-.709-1.029-2.289-1.373-3.867-1.373-3.619 0-6.709 2.741-7.311 6.586-.312 1.918.131 3.752 1.219 5.031 1 1.176 2.426 1.666 4.125 1.666 2.916 0 4.533-1.875 4.533-1.875l-.146.91a.57.57 0 00.564.66h2.949a.95.95 0 00.938-.803l1.771-11.209a.571.571 0 00-.565-.658zm-4.565 6.374c-.314 1.871-1.801 3.127-3.695 3.127-.949 0-1.711-.305-2.199-.883-.484-.574-.666-1.391-.514-2.301.297-1.855 1.805-3.152 3.67-3.152.93 0 1.686.309 2.184.892.501.589.699 1.411.554 2.317zM119.295 7.23l-2.807 17.858a.569.569 0 00.562.658h2.822c.469 0 .867-.34.938-.803l2.768-17.536a.57.57 0 00-.562-.659h-3.16a.571.571 0 00-.561.482z" fill="#179BD7"/>
                          <path d="M7.266 29.154l.523-3.322-1.165-.027H1.061L4.927 1.292a.316.316 0 01.314-.268h9.38c3.114 0 5.263.648 6.385 1.927.526.6.861 1.227 1.023 1.917.17.724.173 1.589.007 2.644l-.012.077v.676l.526.298a3.69 3.69 0 011.065.812c.45.513.741 1.165.864 1.938.127.795.085 1.741-.123 2.812-.24 1.232-.628 2.305-1.152 3.183a6.547 6.547 0 01-1.825 2c-.696.494-1.523.869-2.458 1.109-.906.236-1.939.355-3.072.355h-.73c-.522 0-1.029.188-1.427.525a2.21 2.21 0 00-.744 1.328l-.055.299-.924 5.855-.042.215c-.011.068-.03.102-.058.125a.155.155 0 01-.096.035H7.266z" fill="#253B80"/>
                          <path d="M23.048 7.667c-.028.179-.06.362-.096.55-1.237 6.351-5.469 8.545-10.874 8.545H9.326c-.661 0-1.218.48-1.321 1.132L6.596 26.83l-.399 2.533a.704.704 0 00.695.814h4.881c.578 0 1.069-.42 1.16-.99l.048-.248.919-5.832.059-.32c.09-.572.582-.992 1.16-.992h.73c4.729 0 8.431-1.92 9.513-7.476.452-2.321.218-4.259-.978-5.622a4.667 4.667 0 00-1.336-1.03z" fill="#179BD7"/>
                          <path d="M21.754 7.151a9.757 9.757 0 00-1.203-.267 15.284 15.284 0 00-2.426-.177h-7.352a1.172 1.172 0 00-1.159.992L8.05 17.605l-.045.289a1.336 1.336 0 011.321-1.132h2.752c5.405 0 9.637-2.195 10.874-8.545.037-.188.068-.371.096-.55a6.594 6.594 0 00-1.017-.429 9.045 9.045 0 00-.277-.087z" fill="#222D65"/>
                          <path d="M9.614 7.699a1.169 1.169 0 011.159-.991h7.352c.871 0 1.684.057 2.426.177a9.757 9.757 0 011.481.353c.365.121.704.264 1.017.429.368-2.347-.003-3.945-1.272-5.392C20.378.682 17.853 0 14.622 0h-9.38c-.66 0-1.223.48-1.325 1.133L.01 25.898a.806.806 0 00.795.932h5.791l1.454-9.225 1.564-9.906z" fill="#253B80"/>
                        </svg>
                      </div>
                      <p className="text-center text-sm text-muted-foreground mb-4">
                        You will be redirected to PayPal to complete your payment securely.
                      </p>
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                        <ul className="space-y-2 text-xs text-muted-foreground">
                          <li className="flex items-start">
                            <CheckCircle className="text-primary mr-2 flex-shrink-0 mt-0.5" size={14} />
                            <span>Secure payment processing through PayPal</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle className="text-primary mr-2 flex-shrink-0 mt-0.5" size={14} />
                            <span>Pay with your PayPal balance, bank account, or card</span>
                          </li>
                          <li className="flex items-start">
                            <CheckCircle className="text-primary mr-2 flex-shrink-0 mt-0.5" size={14} />
                            <span>Instant confirmation and receipt</span>
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                    <Button
                      type="button"
                      onClick={handleBackStep}
                      variant="outline"
                      className="flex-1 py-4 md:py-6 text-base md:text-lg"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      className="flex-1 bg-primary hover:bg-primary/90 text-white py-4 md:py-6 text-base md:text-lg shadow-lg shadow-primary/50 hover:shadow-xl shadow-primary/20 transition-all duration-300"
                    >
                      Complete Payment
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && (
            <Card className="shadow-xl shadow-primary/20">
              <CardContent className="p-4 md:p-8 text-center">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4 md:mb-6">
                  <CheckCircle className="text-primary" size={48} />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3 md:mb-4">Payment Successful!</h2>
                <p className="text-base md:text-lg text-muted-foreground mb-6 md:mb-8 px-4">
                  Thank you for your payment. Your transaction has been processed successfully.
                </p>

                <div className="bg-secondary/30 border border-border rounded-lg p-4 md:p-6 mb-6 md:mb-8 text-left">
                  <h3 className="font-semibold text-foreground mb-4">Transaction Details</h3>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex justify-between">
                      <span>Transaction ID:</span>
                      <span className="font-semibold">TXN{Date.now()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Division:</span>
                      <span className="font-semibold">{paymentData.division}</span>
                    </div>
                    {paymentData.plan && (
                      <div className="flex justify-between">
                        <span>Plan:</span>
                        <span className="font-semibold">{paymentData.plan}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Description:</span>
                      <span className="font-semibold">{paymentData.serviceDescription}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Payment Method:</span>
                      <span className="font-semibold">
                        {paymentData.paymentMethod === 'credit-card' ? 'Credit/Debit Card' : 'PayPal'}
                      </span>
                    </div>
                    <div className="flex justify-between text-base md:text-lg font-bold text-primary mt-2 pt-2 border-t border-border">
                      <span>Amount Paid:</span>
                      <span>${parseFloat(paymentData.amount).toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground mb-6 md:mb-8 px-4">
                  A confirmation email with your invoice has been sent to your registered email address.
                  Our team will contact you shortly regarding your service.
                </p>

                <Button
                  onClick={() => window.location.href = '/'}
                  className="bg-primary hover:bg-primary/90 text-white px-6 md:px-8 py-4 md:py-6 text-base md:text-lg shadow-lg shadow-primary/50 hover:shadow-xl shadow-primary/20 transition-all duration-300 w-full sm:w-auto"
                >
                  Return to Home
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </section>
    </div>
  );
};

export default Payment;
